/* eslint-disable require-jsdoc */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from "@simplybenefits/sb-dates";
import {
    TextInput,
    PhoneInput,
    Services,
    Button,
    Text,
    BubbleSelectBlock,
    Modal,
    i18n,
    tokens,
    localizationMaps, DateInput
} from '@simplybenefits/sb-ui-library';
import * as notifier from '../../../actions/notifier.js';
import { updateFormField } from '../../../actions/signup.js';
import { globalStyles } from '../../../theme/index.js';
import Tip from '../../../components/Tip.js';

const insuranceOptions = [
    'Simply Benefits',
    'Desjardins',
    'Great-West Life',
    'Green Shield',
    'Manulife',
    'Pacific Blue Cross',
    'RBC Insurance',
    'RWAM',
    'SSQ',
    'Sun Life',
    'The Co-operators',
    'Other'
];

class UserDetails extends Component {
    state = {
        firstname: '',
        lastname: '',
        addressLineTwo: '',
        addressLineOne: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
        phone: '',
        birthdate: '',
        gender: '',
        maritlStat: '',
        coverageType: '',
        waiveFor: null,
        hasWaived: null,
        waiveCoverage: false,
        waiveCoverageError: false,
        hasRefused: null,
        refuseCoverage: false,
        refuseCoverageError: false,
        comparableCoverageEmployer: null,
        comparableCoverageCarrier: null,
        comparableCoverageCert: null
    };

    componentDidMount() {
        // if we have userDetails then we have already started editing and its a remount
        if (this.props.userDetails) {
            const userDetails = this.props.userDetails;

            this.setState({
                expectedFirstname: userDetails.expectedFirstname || '',
                expectedLastname: userDetails.expectedLastname || '',
                firstname: userDetails.firstname || '',
                lastname: userDetails.lastname || '',
                addressLineTwo: userDetails.addressLineTwo || '',
                addressLineOne: userDetails.addressLineOne || '',
                city: userDetails.city || '',
                province: localizationMaps.fromEnglish('provinces', userDetails.province) || '',
                postalCode: userDetails.postalCode || '',
                maritlStat: userDetails.maritlStat ? localizationMaps.fromEnglish('maritalStatuses', userDetails.maritlStat) : '',
                phone: userDetails.phone || '',
                birthdate: (userDetails.birthdate || userDetails.birthdate === 0) ? dates.formatIncomingDate(userDetails.birthdate) : '',
                gender: userDetails.gender ? localizationMaps.fromEnglish('genders', userDetails.gender) : '',
                coverageType: userDetails.coverageType || '',
                waiveFor: userDetails.waiveFor || null,
                hasWaived: userDetails.hasWaived || null,
                hasRefused: userDetails.hasRefused || null,
                waiveCoverage: userDetails.waiveCoverage,
                refuseCoverage: false,
                comparableCoverageEmployer: userDetails.comparableCoverageEmployer || null,
                comparableCoverageCarrier: userDetails.comparableCoverageCarrier || null,
                comparableCoverageCert: userDetails.comparableCoverageCert || null,
                language: userDetails.language
            });
        }

        return null;
    }

    _onBlur = async (type) => {
        let error = await Services.validateInput('checkEmpty', this.state[type] || '');

        // Make sure that the firstname and last name match the expected name
        if (!this.props.isAffiliate && !error && type === 'firstname') {
            error = this.state.firstname.trim().toUpperCase() !== this.state.expectedFirstname.trim().toUpperCase() ? i18n.t(tokens.labels.signup.expectingNameError, { expectedName: this.state.expectedFirstname }) : false;
        }

        if (!this.props.isAffiliate && !error && type === 'lastname') {
            error = this.state.lastname.trim().toUpperCase() !== this.state.expectedLastname.trim().toUpperCase() ? i18n.t(tokens.labels.signup.expectingNameError, { expectedName: this.state.expectedLastname }) : false;
        }

        if (error) {
            this.setState({
                [`${ type }Error`]: error
            });
        } else {
            this.props.dispatch(updateFormField('userDetails', type, this.state[type]));
        }

        return null;
    };

    _handleBubbleChange = (type, value) => {
        this.props.dispatch(updateFormField('userDetails', type, value));

        // make the saved state to false, since we have made edits
        this.props.setSavedState('userDetails', false);

        this.setState({
            [type]: value
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('userDetails', false);

        if (type === 'comparableCoverageCarrier' && !isValue && event.target.value === 'Other') {
            event.target.value = '';
        }

        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };
    
    _toggleWaiveConfirmation = () => {
        this.setState({
            showWaiveConfirmation: !this.state.showWaiveConfirmation
        });
    };

    _toggleRefuseConfirmation = () => {
        this.setState({
            showRefuseConfirmation: !this.state.showRefuseConfirmation
        });
    };

    _saveDetails = () => {
        const userDetails = {
            firstname: this.state.firstname.trim(),
            lastname: this.state.lastname.trim(),
            addressLineOne: this.state.addressLineOne.trim(),
            addressLineTwo: this.state.addressLineTwo.trim(),
            city: this.state.city.trim(),
            postalCode: this.state.postalCode,
            province: this.state.province.trim(),
            birthdate: dates.formatOutgoingDate(this.state.birthdate),
            phone: this.state.phone.trim(),
            gender: this.state.gender.trim(),
            maritlStat: this.state.maritlStat.trim(),
            coverageType: this.state.coverageType.trim(),
            waiveFor: this.state.waiveFor,
            hasWaived: this.state.hasWaived,
            hasRefused: this.state.hasRefused,
            comparableCoverageEmployer: this.state.comparableCoverageEmployer || null,
            comparableCoverageCarrier: this.state.comparableCoverageCarrier || null,
            comparableCoverageCert: this.state.comparableCoverageCert || null
        };

        // make the saved state to true, we have hit next and everything is valid
        this.props.setSavedState('userDetails', true);

        this.props.stepCompletion({ ...userDetails }, 'userDetails', false);
        return false;
    };

    _validateForm = async (skipCheck) => {
        let firstnameError = await Services.validateInput('firstname', this.state.firstname);
        let lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const phoneError = await Services.validateInput('phone', this.state.phone);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);
        const maritlStatError = await Services.validateInput('maritlStat', this.state.maritlStat);
        const genderError = await Services.validateInput('gender', this.state.gender);
        const birthdateError = await Services.validateInput('checkEmpty', this.state.birthdate);

        let addressLineOneError = await Services.validateInput('addressLineOne', this.state.addressLineOne);
        let waiveCoverageError = false;
        let refuseCoverageError = false;

        if (this.state.waiveCoverage) {
            if (!this.state.waiveFor || !this.state.hasWaived) {
                waiveCoverageError = i18n.t(tokens.labels.signup.selectOptionForEachSection);
            }
        }

        if (this.state.refuseCoverage) {
            if (!this.state.hasRefused) {
                refuseCoverageError = i18n.t(tokens.labels.signup.selectOptionBeforeContinuing);
            }
        }

        // Make sure that the firstname and last name match the expected name
        if (!this.props.isAffiliate && !firstnameError && !lastnameError) {
            firstnameError = this.state.firstname.trim().toUpperCase() !== this.state.expectedFirstname.trim().toUpperCase() ? i18n.t(tokens.labels.signup.expectingNameError, { expectedName: this.state.expectedFirstname }) : false;
            lastnameError = this.state.lastname.trim().toUpperCase() !== this.state.expectedLastname.trim().toUpperCase() ? i18n.t(tokens.labels.signup.expectingNameError, { expectedName: this.state.expectedLastname }) : false;
        }

        this.setState({
            firstnameError,
            lastnameError,
            phoneError,
            addressLineOneError,
            cityError,
            provinceError,
            postalCodeError,
            genderError,
            maritlStatError,
            birthdateError,
            waiveCoverageError,
            refuseCoverageError
        });

        if (
            phoneError ||
            addressLineOneError ||
            cityError ||
            provinceError ||
            postalCodeError ||
            maritlStatError ||
            genderError ||
            birthdateError ||
            waiveCoverageError ||
            refuseCoverageError ||
            firstnameError ||
            lastnameError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.pleaseConfirmDetailsError) }));
            return false;
        }

        if (!skipCheck && this.state.refuseCoverage) {
            this._toggleRefuseConfirmation();
            return null;
        }

        if (!skipCheck && this.state.waiveCoverage) {
            this._toggleWaiveConfirmation();
            return null;
        }

        // if we have waived confirm they have provided comparable coverage
        if (this.state.waiveCoverage) {
            const comparableCoverageEmployerError = !this.state.comparableCoverageEmployer || this.state.comparableCoverageEmployer.trim().length === 0 ? i18n.t(tokens.labels.signup.provideComparableCoverageOrganization) : false;
            const comparableCoverageCarrierError = !this.state.comparableCoverageCarrier || this.state.comparableCoverageCarrier.trim().length === 0 ? i18n.t(tokens.labels.signup.provideComparableCoverageInsuranceCarrier) : false;
            const comparableCoverageCertError = !this.state.comparableCoverageCert || this.state.comparableCoverageCert.trim().length === 0 ? i18n.t(tokens.labels.signup.provideComparableCoverageCertificationNumber) : false;

            this.setState({
                comparableCoverageEmployerError,
                comparableCoverageCarrierError,
                comparableCoverageCertError
            });

            if (comparableCoverageEmployerError || comparableCoverageCarrierError || comparableCoverageCertError) {
                return null;
            }
        }

        return this._saveDetails();
    };

    /**
     * 
     * @param {*} input 
     */
    _formatPostalCode = async (input) => {
        const formattedPostalCode = input
            .toUpperCase()
            .trim()
            .replace(/\s+/g, '') // Remove all spaces
            .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters

        await this._handleInputChange('postalCode', formattedPostalCode, true);
        this._onBlur('postalCode');
    };

    render() {
        const {
            birthdate,
            coverageType,
            firstname,
            lastname,
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode,
            phone,
            gender,
            maritlStat,
            comparableCoverageCarrier,
            comparableCoverageEmployer,
            comparableCoverageCert
        } = this.state;

        const isAffiliate = this.props.isAffiliate;
        const readableBirthdate = isAffiliate && (!birthdate) ? '' : dates.getStartOfDay(birthdate, 'll', i18n.locale);

        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            },
            visibilityIcon: {
                position: 'absolute',
                marginLeft: -30
            },
            stepperBackBtn: {
                margin: '0px auto 0px 0px !important'
            },
            stepperBtnContainer: globalStyles.stepperBtnContainer,
            stepperFinishBtn: globalStyles.stepperFinishBtn,
            stepperNextBtn: globalStyles.stepperNextBtn
        };

        const refusalString = this.state.hasRefused?.toUpperCase() === 'HEALTH-DENTAL' ?
            i18n.t(tokens.labels.signup.dependentLifeExtendedHealthCareAndDental) :
            this.state.hasRefused?.toUpperCase() === 'HEALTH' ? i18n.t(tokens.labels.signup.dependentLifeAndExtendedHealthCare) : i18n.t(tokens.labels.signup.dependentLifeAndDental);

        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.titles.details) }</Text>

                <div style={styles.form}>
                    <TextInput
                        label={ i18n.t(tokens.labels.firstname) }
                        value={firstname}
                        onBlur={() => this._onBlur('firstname')}
                        onChange={(e) => this._handleInputChange('firstname', e)}
                        error={ this.state.firstnameError }
                        fullWidth
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.lastname) }
                        value={lastname}
                        onBlur={() => this._onBlur('lastname')}
                        onChange={(e) => this._handleInputChange('lastname', e)}
                        error={ this.state.lastnameError }
                        fullWidth
                    />

                    {
                        isAffiliate ?
                            <DateInput
                                label={ i18n.t(tokens.labels.birthdate) }
                                value={ birthdate }
                                onBlur={() => this._onBlur('birthdate')}
                                onChange={(e) => this._handleInputChange('birthdate', e)}
                                error={Boolean(this.state.birthdateError)}
                                helperText={this.state.birthdateError}
                                fullWidth
                                required
                            />
                            :
                            <TextInput
                                label={ i18n.t(tokens.labels.birthdate) }
                                value={ readableBirthdate }
                                disabled
                                fullWidth
                            />
                    }

                    <TextInput
                        label={ i18n.t(tokens.labels.gender) }
                        fullWidth
                        required
                        value={gender}
                        onBlur={() => this._onBlur('gender')}
                        onChange={(e) => this._handleInputChange('gender', e)}
                        error={Boolean(this.state.genderError)}
                        helperText={this.state.genderError}
                        select
                        selectOptions={ Services.selectFieldOptions.genderOptions() }
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.maritalStatus) }
                        fullWidth
                        required
                        value={maritlStat}
                        onBlur={() => this._onBlur('maritlStat')}
                        onChange={(value) => this._handleInputChange('maritlStat', value)}
                        error={Boolean(this.state.maritlStatError)}
                        helperText={this.state.maritlStatError}
                        select
                        selectOptions={Services.selectFieldOptions.maritalStatusOptions() }
                    />

                    <PhoneInput
                        label={ i18n.t(tokens.labels.phoneNumber) }
                        value={phone}
                        autoComplete="tel"
                        onBlur={() => this._onBlur('phone')}
                        onChange={(value) => this._handleInputChange('phone', value)}
                        error={Boolean(this.state.phoneError)}
                        helperText={this.state.phoneError}
                        fullWidth
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.addressLineOne)}
                        autocomplete="shipping street-address"
                        value={addressLineOne}
                        onBlur={() => this._onBlur('addressLineOne')}
                        onChange={(value) => this._handleInputChange('addressLineOne', value)}
                        error={Boolean(this.state.addressLineOneError)}
                        helperText={this.state.addressLineOneError}
                        fullWidth
                    />

                    <div style={styles.row}>
                        <TextInput
                            label={ i18n.t(tokens.labels.addressLineTwo)}
                            value={addressLineTwo}
                            onBlur={() => this._onBlur('addressLineTwo')}
                            onChange={(value) => this._handleInputChange('addressLineTwo', value)}
                            fullWidth
                            helperText={i18n.t(tokens.labels.addressLineTwoExample)}
                        />

                        <div style={styles.divider} />

                        <TextInput
                            label={ i18n.t(tokens.labels.account.cityTown)}
                            autocomplete="shipping locality"
                            value={city}
                            onBlur={() => this._onBlur('city')}
                            onChange={(value) => this._handleInputChange('city', value)}
                            error={Boolean(this.state.cityError)}
                            helperText={this.state.cityError}
                            fullWidth
                        />
                    </div>

                    <div style={styles.row}>
                        <TextInput
                            label={ i18n.t(tokens.labels.province) }
                            autocomplete="shipping region"
                            value={province}
                            onBlur={() => this._onBlur('province')}
                            onChange={(value) => this._handleInputChange('province', value)}
                            error={ this.state.provinceError}
                            select
                            selectOptions={Services.selectFieldOptions.provinceOptions() }
                            fullWidth
                        />

                        <div style={styles.divider} />

                        <TextInput
                            label={ i18n.t(tokens.labels.postalCode) }
                            autocomplete="shipping postal-code"
                            value={postalCode}
                            onBlur={(e) => this._formatPostalCode(e.target.value)}
                            onChange={(e) => {
                                // Allow typing only 6 alphanumeric characters and trim any excess
                                const postalCodeValue = e.target.value
                                    .toUpperCase()
                                    .slice(0, 7);
                                this._handleInputChange('postalCode', postalCodeValue, true);
                            }}
                            error={Boolean(this.state.postalCodeError)}
                            helperText={ this.state.postalCodeError }
                            fullWidth
                        />
                    </div>

                    {
                        !this.props.isMarketplaceOnly &&
                        <>
                            <Tip
                                right
                                show={ this.props.tips.coverageBreakdown }
                                close={ () => this.props.hideTip('coverageBreakdown') }
                                customStyle={{ marginTop: 200 }}
                                formatted
                            >
                                { i18n.t(tokens.labels.signup.coverageSelectionInstructions) }<br/><br/>
                                <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.solo) }</span> { i18n.t(tokens.labels.signup.justYou) }<br/><br/>
                                <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.duo) }</span> { i18n.t(tokens.labels.signup.youAndPartnerOrChild) }<br/><br/>
                                <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.family) }</span> { i18n.t(tokens.labels.signup.twoOrMoreDependents) }<br/><br/>
                                <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.commonLawDefinition) }</span> { i18n.t(tokens.labels.signup.commonLawLiving) }<br/>
                                <ul>
                                    <li>{ i18n.t(tokens.labels.signup.livingWithYou) }</li>
                                    <li>{ i18n.t(tokens.labels.signup.parentByBirthOrAdoption) }</li>
                                    <li>{ i18n.t(tokens.labels.signup.custodyOrControl) }</li>
                                </ul>
                            </Tip>

                            <Tip
                                left
                                show={ this.props.tips.enrolledElseWhere }
                                close={ () => this.props.hideTip('enrolledElseWhere') }
                                customStyle={{ marginTop: 750 }}
                            >
                                <span>{ i18n.t(tokens.labels.signup.youOrDependents) }</span>
                                <span style={{ fontWeight: 'bold' }}> { i18n.t(tokens.labels.signup.enrolledInAnotherHealthPlan) }</span>
                                <span>, { i18n.t(tokens.labels.signup.waiveYourExtendedHealthCare) }</span>
                                <span style={{ fontWeight: 'bold' }}> { i18n.t(tokens.labels.signup.coordinateYourHealthPlan) }</span>
                                <span> { i18n.t(tokens.labels.signup.withYourDependentsToMaximizeCoverage) }</span>
                            </Tip>

                            <TextInput
                                label={ i18n.t(tokens.labels.coverageType)}
                                value={coverageType}
                                fullWidth
                                onBlur={() => this._onBlur('coverageType')}
                                onChange={(value) => this._handleInputChange('coverageType', value)}
                                error={Boolean(this.state.coverageTypeError)}
                                helperText={this.state.coverageTypeError}
                                select
                                selectOptions={ Services.selectFieldOptions.coverageOptions() }
                                disabled={ this.props.userDetails.blockEnrolmentCoverageTypeChanges === 1 }
                            />

                            {
                                Boolean(this.props.initData.allowRefusals) &&
                                <div>
                                    <Text medium margin="20px 0 0 0">
                                        { i18n.t(tokens.labels.signup.coverageDeclineInstructions) }
                                    </Text>

                                    {
                                        this.state.refuseCoverageError &&
                                        <Text bold small margin="20px 0 10px 0" color="red">
                                            { this.state.refuseCoverageError }
                                        </Text>
                                    }

                                    <BubbleSelectBlock
                                        first={{
                                            title: i18n.t(tokens.labels.signup.refuse),
                                            selected: this.state.refuseCoverage === true,
                                            field: 'refuseCoverageYes',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        second={{
                                            title: i18n.t(tokens.labels.signup.doNotRefuse),
                                            selected: !this.state.refuseCoverage,
                                            field: 'refuseCoverageNo',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        handleChange={ (field) => {
                                            if (field === 'refuseCoverageNo') {
                                                this._handleBubbleChange('refuseCoverage', false);
                                                this._handleBubbleChange('hasRefused', null);
                                            } else {
                                                this._handleBubbleChange('refuseCoverage', true);
                                            }
                                        }}
                                    />
                                </div>
                            }

                            {
                                this.state.refuseCoverage &&
                                <React.Fragment>
                                    <Text bold small margin="20px 0 10px 0">
                                        { i18n.t(tokens.labels.signup.iAmRefusingForTheFollowingBenefits) }
                                    </Text>

                                    <BubbleSelectBlock
                                        first={{
                                            title: i18n.t(tokens.labels.signup.extendedHealthCare),
                                            selected: this.state.hasRefused === 'Health',
                                            field: 'hasRefusedHealth',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        second={{
                                            title: i18n.t(tokens.titles.dental),
                                            selected: this.state.hasRefused === 'Dental',
                                            field: 'hasRefusedDental',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        third={{
                                            title: i18n.t(tokens.labels.signup.both),
                                            selected: this.state.hasRefused === 'Health-Dental',
                                            field: 'hasRefusedBoth',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        handleChange={ async (field) => {
                                            if (field === 'hasRefusedHealth') {
                                                await this._handleBubbleChange('hasRefused', 'Health');
                                            } else if (field === 'hasRefusedDental') {
                                                await this._handleBubbleChange('hasRefused', 'Dental');
                                            } else if (field === 'hasRefusedBoth') {
                                                await this._handleBubbleChange('hasRefused', 'Health-Dental');
                                            }
                                        }}
                                    />
                                </React.Fragment>
                            }

                            {
                                !this.state.refuseCoverage &&
                                <div>
                                    <Text medium margin="20px 0 0 0">
                                        { i18n.t(tokens.labels.signup.extendedHealthAndDentalCoverage) }
                                    </Text>

                                    {
                                        this.state.waiveCoverageError &&
                                        <Text bold small margin="20px 0 10px 0" color="red">
                                            { this.state.waiveCoverageError }
                                        </Text>
                                    }

                                    <BubbleSelectBlock
                                        first={{
                                            title: i18n.t(tokens.buttons.waive),
                                            selected: this.state.waiveCoverage === true,
                                            field: 'waiveCoverageYes',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        second={{
                                            title: i18n.t(tokens.buttons.doNotWaive),
                                            selected: !this.state.waiveCoverage,
                                            field: 'waiveCoverageNo',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        handleChange={ (field) => {
                                            if (field === 'waiveCoverageNo') {
                                                this._handleBubbleChange('waiveCoverage', false);
                                                this._handleBubbleChange('waiveFor', null);
                                                this._handleBubbleChange('hasWaived', null);

                                                this._handleInputChange('comparableCoverageEmployer', null, true);
                                                this._handleInputChange('comparableCoverageCarrier', null, true);
                                                this._handleInputChange('comparableCoverageCert', null, true);
                                            } else {
                                                this._handleBubbleChange('waiveCoverage', true);
                                            }
                                        }}
                                    />
                                </div>
                            }

                            {
                                this.state.waiveCoverage &&
                                <React.Fragment>
                                    <Text bold small margin="20px 0 10px 0">
                                        { i18n.t(tokens.labels.signup.waiveCoverageFor) }
                                    </Text>

                                    <BubbleSelectBlock
                                        allowToggleOff={true}
                                        first={{
                                            title: i18n.t(tokens.labels.signup.myselfAndDependents),
                                            selected: this.state.waiveFor === 'Self-Dependents',
                                            field: 'waiveSelfAndDependents',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        second={{
                                            title: i18n.t(tokens.labels.signup.dependentsOnly),
                                            selected: this.state.waiveFor === 'Dependents',
                                            field: 'waiveDependents',
                                            customStyle: {
                                                fontSize: 12
                                            }
                                        }}
                                        handleChange={ async (field) => {
                                            if (field === 'waiveSelfAndDependents') {
                                                if (this.state.waiveFor === 'Self-Dependents') {
                                                    await this._handleBubbleChange('waiveFor', null);
                                                } else {
                                                    await this._handleBubbleChange('waiveFor', 'Self-Dependents');
                                                }
                                            } else if (field === 'waiveDependents') {
                                                if (this.state.waiveFor === 'Dependents') {
                                                    await this._handleBubbleChange('waiveFor', null);
                                                } else {
                                                    await this._handleBubbleChange('waiveFor', 'Dependents');
                                                }
                                            }
                                        }}
                                    />

                                    {
                                        this.state.waiveFor &&
                                        <React.Fragment>
                                            <Text bold small margin="20px 0 10px 0">
                                                { i18n.t(tokens.labels.signup.forTheFollowingBenefits) }
                                            </Text>

                                            <BubbleSelectBlock
                                                first={{
                                                    title: i18n.t(tokens.labels.signup.extendedHealthCare),
                                                    selected: this.state.hasWaived === 'Health',
                                                    field: 'hasWaivedHealth',
                                                    customStyle: {
                                                        fontSize: 12
                                                    }
                                                }}
                                                second={{
                                                    title: i18n.t(tokens.titles.dental),
                                                    selected: this.state.hasWaived === 'Dental',
                                                    field: 'hasWaivedDental',
                                                    customStyle: {
                                                        fontSize: 12
                                                    }
                                                }}
                                                third={{
                                                    title: i18n.t(tokens.labels.signup.both),
                                                    selected: this.state.hasWaived === 'Health-Dental',
                                                    field: 'hasWaivedBoth',
                                                    customStyle: {
                                                        fontSize: 12
                                                    }
                                                }}
                                                handleChange={ async (field) => {
                                                    if (field === 'hasWaivedHealth') {
                                                        await this._handleBubbleChange('hasWaived', 'Health');
                                                    } else if (field === 'hasWaivedDental') {
                                                        await this._handleBubbleChange('hasWaived', 'Dental');
                                                    } else if (field === 'hasWaivedBoth') {
                                                        await this._handleBubbleChange('hasWaived', 'Health-Dental');
                                                    }
                                                }}
                                            />
                                        </React.Fragment>
                                    }
                                </React.Fragment>
                            }
                        </>
                    }

                    <div style={styles.stepperBtnContainer}>
                        <Button
                            style={styles.stepperNextBtn}
                            onClick={() => this._validateForm(false)}
                            color="primary"
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>
                    </div>
                </div>

                <Modal
                    open={this.state.showRefuseConfirmation}
                    onClose={() => this._toggleRefuseConfirmation()}
                    title={ i18n.t(tokens.titles.areYouSure) }
                    primaryButton={{
                        label: i18n.t(tokens.buttons["refuse coverage"]),
                        onClick: () => this._validateForm(true)
                    }}
                    loader={this.props.loader}
                >
                    <Text medium display="inline-block" >
                        { i18n.t(tokens.labels.signup.byRefusingCoverageUnderstandPlan, { refusalString })}
                    </Text>

                    <Text medium display="inline-block" margin="10px 0">
                        { i18n.t(tokens.labels.signup.understandLiabilityForDepLifeClaims, { company: this.props.initData.company }) }
                    </Text>

                    <Text medium display="inline-block" >
                        { i18n.t(tokens.labels.signup.mustSubmitEvidenceOfInsurability) }
                    </Text>
                </Modal>

                <Modal
                    open={Boolean(this.state.showWaiveConfirmation)}
                    onClose={() => this._toggleWaiveConfirmation()}
                    title={ i18n.t(tokens.titles.areYouSure) }
                    primaryButton={{
                        label: i18n.t(tokens.buttons.waive),
                        onClick: () => this._validateForm(true)
                    }}
                    loader={this.props.loader}
                >
                    {
                        this.state.waiveFor === 'Self-Dependents' ?
                            <Text medium display="inline-block" >
                                { i18n.t(tokens.labels.signup.waivingCoverageForYourselfAndYourDependents) }
                            </Text>
                            :
                            <Text medium display="inline-block" >
                                { i18n.t(tokens.labels.signup.waivingCoverageForYourDependents) }
                            </Text>
                    }

                    <TextInput
                        label={ i18n.t(tokens.labels.signup.organization) }
                        value={comparableCoverageEmployer || ''}
                        fullWidth
                        onBlur={() => this._onBlur('comparableCoverageEmployer') }
                        onChange={(value) => this._handleInputChange('comparableCoverageEmployer', value)}
                        error={ Boolean(this.state.comparableCoverageEmployerError)}
                        helperText={ this.state.comparableCoverageEmployerError }
                        required={ true }
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.signup.insuranceCarrier) }
                        value={comparableCoverageCarrier }
                        fullWidth
                        onBlur={() => this._onBlur('comparableCoverageCarrier') }
                        onChange={(value) => this._handleInputChange('comparableCoverageCarrier', value) }
                        error={ Boolean(this.state.comparableCoverageCarrierError) }
                        helperText={ this.state.comparableCoverageCarrierError }
                        select={
                            (!comparableCoverageCarrier || insuranceOptions.indexOf(comparableCoverageCarrier) > -1)
                            && comparableCoverageCarrier !== ''
                        }
                        selectOptions={insuranceOptions}
                        required={ true }
                    />

                    <TextInput
                        label={ i18n.t(tokens.labels.signup.certificateNumber) }
                        value={comparableCoverageCert || ''}
                        fullWidth
                        onBlur={() => this._onBlur('comparableCoverageCert')}
                        onChange={(value) => this._handleInputChange('comparableCoverageCert', value)}
                        error={ Boolean(this.state.comparableCoverageCertError) }
                        helperText={ this.state.comparableCoverageCertError }
                        required={ true }
                    />
                </Modal>
            </div>
        );
    }
}

UserDetails.propTypes = {
    firstname: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    lastname: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    province: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    phone: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    birthdate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    gender: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    maritlStat: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    coverageType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ])
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    userDetails: state.signup.userDetails,
    status: state
});

export default (connect(mapStateToProps)(UserDetails));